import React, { useState } from 'react';
import ImageUploaderComponent from '../../../../components/ImageUploader';
import { uploadFileToS3 } from '../../../../utility/S3Uploader';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import { Copy, Check, Loader2 } from 'lucide-react';
import { useToast } from '../../../../hooks/use-toast';
// Import the MediaLibraryModal (Adjust path if necessary based on where you saved it)
import MediaLibraryModal from '../../../../components/MediaLibraryModal'; 

const SeoMediaTools = () => {
  const { toast } = useToast();
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  
  // State for the modal
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const S3_BASE_URL = process.env.REACT_APP_S3_ASSETS_FETCH_URL || "https://cdn.selyst.com"; 

  const handleUpload = async (file) => {
    if (!file) {
        setUploadedUrl('');
        return;
    }

    setUploading(true);
    try {
        const timestamp = Date.now();
        const fileExt = file.name.split('.').pop();
        const fileNameBase = file.name.substring(0, file.name.lastIndexOf('.'));
        const sanitizedBase = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-');
        const newFileName = `${sanitizedBase}-${timestamp}.${fileExt}`;

        const renamedFile = new File([file], newFileName, { type: file.type });
        const s3Path = await uploadFileToS3(renamedFile, "content/assets");
        
        // 2. Construct Full URL for Sanity
        let fullUrl = s3Path;
        if (!s3Path.startsWith('http')) {
             // Ensure clean slash handling
            const cleanBase = S3_BASE_URL.replace(/\/$/, '');
            const cleanPath = s3Path.replace(/^\//, '');
            fullUrl = `${cleanBase}/${cleanPath}`;
        }

        setUploadedUrl(fullUrl);
        toast({ title: "Success", description: "Image uploaded successfully." });

    } catch (error) {
        console.error("Upload error:", error);
        toast({ 
            title: "Upload Failed", 
            description: error.message, 
            variant: "destructive" 
        });
    } finally {
        setUploading(false);
    }
  };

  const handleLibrarySelect = (url) => {
      // The modal returns the full S3 URL.
      // Optional: If you strictly want CDN URLs, you could replace the S3 domain here.
      // For now, we just use what the library returns.
      setUploadedUrl(url);
      setIsLibraryOpen(false);
      toast({ title: "Selected", description: "Image selected from library." });
  };

  const copyToClipboard = () => {
    if (!uploadedUrl) return;
    navigator.clipboard.writeText(uploadedUrl);
    setIsCopied(true);
    toast({ description: "Copied to clipboard" });
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto h-full overflow-y-auto">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Media Tools</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Upload Asset for Sanity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md text-sm text-blue-800 border border-blue-100">
            <p className="font-semibold mb-2">Workflow:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Upload an image below OR browse existing images.</li>
              <li>Copy the generated URL.</li>
              <li>Paste it into Sanity's <strong>"SEO Image (S3 Link)"</strong> field.</li>
            </ol>
          </div>

          {/* Uploader Section */}
          <div className="border-2 border-dashed rounded-lg p-6 relative bg-gray-50/50">
             {uploading && (
                <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded-lg">
                    <div className="flex flex-col items-center">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2"/>
                        <span className="text-sm font-medium text-blue-800">Uploading...</span>
                    </div>
                </div>
             )}
             
             <ImageUploaderComponent 
                maxFiles={1}
                onChange={handleUpload} 
                note="Uploads to: content/assets/"
                // Enable the Browse Button
                onBrowse={() => setIsLibraryOpen(true)} 
             />
          </div>

          {/* Result Section */}
          {uploadedUrl && (
            <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 pt-4 border-t">
              <Label>Public URL (Ready for Sanity)</Label>
              <div className="flex items-center space-x-2">
                <Input value={uploadedUrl} readOnly className="font-mono text-sm bg-gray-100" />
                <Button onClick={copyToClipboard} variant="outline" className="w-24 shrink-0">
                  {isCopied ? <Check className="w-4 h-4 text-green-600" /> : <><Copy className="w-4 h-4 mr-2" /> Copy</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Media Library Modal */}
      <MediaLibraryModal 
        open={isLibraryOpen} 
        onOpenChange={setIsLibraryOpen} 
        onSelect={handleLibrarySelect}
      />
    </div>
  );
};

export default SeoMediaTools;